import * as THREE from 'three';
import { BIMState } from '../state/useBIMStore';

export type HostType = 'Face' | 'WorkPlane' | 'Element';

export interface HostResult {
    elementId: string;
    point: THREE.Vector3;
    normal: THREE.Vector3;
    hostType: HostType;
    isValid: boolean;
}

export interface HostFilter {
    categories?: string[];
    rejectId?: string; // Ignore specific element (e.g., self)
}

export class HostManager {
    constructor(private bimStore: BIMState) { }

    /**
     * Find a host element under the cursor
     */
    findHost(
        intersections: THREE.Intersection[],
        filter?: HostFilter
    ): HostResult | null {
        return this.resolveHost(intersections, filter);
    }

    /**
     * Find a host by raycasting the scene directly.
     *
     * R3F only raycasts objects participating in its event system; BIM meshes
     * without pointer handlers never appear in event.intersections, so hosted
     * tools (doors/windows) must raycast the scene themselves.
     */
    findHostByRay(
        scene: THREE.Scene,
        ray: THREE.Ray,
        filter?: HostFilter
    ): HostResult | null {
        const raycaster = new THREE.Raycaster();
        raycaster.ray.copy(ray);
        return this.resolveHost(raycaster.intersectObjects(scene.children, true), filter);
    }

    private resolveHost(
        intersections: THREE.Intersection[],
        filter?: HostFilter
    ): HostResult | null {
        if (!intersections || intersections.length === 0) return null;

        // Iterate through intersections to find valid host
        for (const intersect of intersections) {
            const elementId = this.resolveElementId(intersect);

            if (!elementId) continue;
            if (filter?.rejectId && elementId === filter.rejectId) continue;

            const element = this.bimStore.project.elements.get(elementId);
            if (!element) continue;

            // Check Category Filter
            if (filter?.categories && !filter.categories.includes(element.category)) {
                continue;
            }

            // Valid Host Found
            return {
                elementId: element.id,
                point: intersect.point,
                normal: intersect.face?.normal?.clone().applyQuaternion(intersect.object.quaternion) || new THREE.Vector3(0, 0, 1),
                hostType: 'Element',
                isValid: true
            };
        }

        return null;
    }

    private resolveElementId(intersect: THREE.Intersection): string | null {
        // Instanced batches carry a per-instance element id list on the mesh
        if (intersect.instanceId != null) {
            let obj: THREE.Object3D | null = intersect.object;
            while (obj) {
                const ids = obj.userData?.bimInstanceIds as string[] | undefined;
                const instanceId = ids?.[intersect.instanceId];
                if (instanceId) return instanceId;
                obj = obj.parent;
            }
        }

        let current: THREE.Object3D | null = intersect.object;
        while (current) {
            if (current.userData && current.userData.id) {
                return current.userData.id;
            }
            current = current.parent;
        }
        return null;
    }

    /**
     * Validate compatibility between a hosted element category and a host instance
     */
    validateHost(hostId: string, hostedCategory: string): boolean {
        const host = this.bimStore.project.elements.get(hostId);
        if (!host) return false;

        // Rules based on BIM logic
        switch (hostedCategory) {
            case 'Doors':
            case 'Windows':
                return host.category === 'Walls'; // Must be hosted by Wall
            case 'Structural Framing': // Beams
            case 'Structural Trusses':
                // Can be hosted by Levels (common) or Faces (uncommon for basic beams but possible)
                // Usually referenced to levels, not hosted by elements directly in this context
                return true;
            case 'Air Terminals':
            case 'Lighting Fixtures':
                return ['Ceilings', 'Floors', 'Walls', 'Faces'].includes(host.category);
            case 'Rebar':
                return ['Floors', 'Walls', 'Structural Columns', 'Structural Framing', 'Structural Foundations'].includes(host.category);
            default:
                return true; // Default to allow
        }
    }
}
